import * as React from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
import { FenceSegment } from "models/fenceSegment";
import { FenceGate, GateSizeEnum } from "models/fenceGate";
import { EstimatePageCurrentMode } from "pages/EstimatePage";

type FenceSegmentCardProps = {
  handleGateDelete: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleAddGate: (fenceSegmentId: string, size: GateSizeEnum) => React.MouseEventHandler<HTMLElement>;
  handleEditStyle: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleDeleteStyle: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleCancelGateAdd: (e: React.MouseEvent<HTMLButtonElement>) => void;

  fenceSegment: FenceSegment;
  isSelected: boolean;
  currentPageMode: EstimatePageCurrentMode;
};

function FenceSegmentCard(props: FenceSegmentCardProps) {
  const isAddingGate =
    (props.currentPageMode === EstimatePageCurrentMode.AddDoubleGate ||
      props.currentPageMode === EstimatePageCurrentMode.AddSingleGate) &&
    props.isSelected;

  return (
    <Card className="shadow-sm mb-2">
      <CardHeader className="bg-primary text-white d-flex align-items-start justify-content-between py-2">
        <span className="small fw-600" style={{ lineHeight: 1.4 }}>
          {props.fenceSegment.calculateLinearFeet()} LFT &middot; {props.fenceSegment.material.label},{" "}
          {props.fenceSegment.style.label}, {props.fenceSegment.color.label}, {props.fenceSegment.height.label} FT
        </span>
        <Button
          color="danger"
          size="sm"
          outline
          className="ms-2 flex-shrink-0 text-danger border-danger"
          onClick={props.handleDeleteStyle}
          value={props.fenceSegment.id}
        >
          ✕
        </Button>
      </CardHeader>
      <CardBody className="py-2">
        {isAddingGate ? (
          <div className="d-flex justify-content-center">
            <Button color="danger" outline size="sm" onClick={props.handleCancelGateAdd} value={props.fenceSegment.id}>
              Cancel Adding Gate
            </Button>
          </div>
        ) : (
          <>
            <FenceGateList gates={props.fenceSegment.gates} onGateDelete={props.handleGateDelete} />
            <div className="d-flex gap-2 mt-2">
              <Button
                outline
                color="primary"
                size="sm"
                className="flex-fill"
                onClick={props.handleEditStyle}
                value={props.fenceSegment.id}
              >
                Edit Style
              </Button>
              <AddGate fenceSegmentId={props.fenceSegment.id} onDropDownSelection={props.handleAddGate} />
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

type AddGateProps = {
  onDropDownSelection(fenceSegmentId: string, size: GateSizeEnum): React.MouseEventHandler<HTMLElement>;
  fenceSegmentId: string;
};
function AddGate(props: AddGateProps) {
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  return (
    <Dropdown isOpen={dropdownOpen} toggle={() => setDropdownOpen((s) => !s)} className="flex-fill">
      <DropdownToggle color="secondary" size="sm" className="w-100 text-dark" caret>
        Add Gate
      </DropdownToggle>
      <DropdownMenu>
        <DropdownItem onClick={props.onDropDownSelection(props.fenceSegmentId, GateSizeEnum.SINGLE)}>
          Single Width Gate
        </DropdownItem>
        <DropdownItem onClick={props.onDropDownSelection(props.fenceSegmentId, GateSizeEnum.DOUBLE)}>
          Double Width Gate
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
}

type FenceGateListProps = {
  onGateDelete: (e: React.MouseEvent<HTMLButtonElement>) => void;
  gates: FenceGate[];
};
function FenceGateList(props: FenceGateListProps) {
  if (props.gates.length === 0) return null;
  return (
    <ul className="list-unstyled mb-0">
      {props.gates.map((g) => (
        <li key={g.id} className="d-flex justify-content-between align-items-center py-1 border-bottom">
          <span className="small">{g.size === GateSizeEnum.DOUBLE ? "Double Width" : "Single Width"} Gate</span>
          <Button color="danger" outline size="sm" value={g.id} onClick={props.onGateDelete}>
            ✕
          </Button>
        </li>
      ))}
    </ul>
  );
}

export { FenceSegmentCard };
